<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { BellIcon, CursorClickIcon } from '@heroicons/vue/outline';
import type { AuraAction } from '@/types';
import AuraRichText from './AuraRichText.vue';

const props = defineProps<{
    action: AuraAction;
    disabled?: boolean;
    loading?: boolean;
}>();
const { t } = useI18n();

const emit = defineEmits<{
    execute: [action: AuraAction];
}>();

const isClickable = computed(
    () =>
        props.action.executionOwner === 'aura' &&
        Boolean(props.action.followUpQuestion?.trim()) &&
        !props.disabled &&
        !props.loading,
);

const priority = computed(() => {
    if (props.action.priority === 'high') {
        return { label: t('aura.cards.action.priority.high'), color: '#48DDC3', text: '#242B59' };
    }

    if (props.action.priority === 'low') {
        return { label: t('aura.cards.action.priority.low'), color: '#E5E7EB', text: '#626468' };
    }

    return { label: t('aura.cards.action.priority.medium'), color: '#F5B041', text: '#242B59' };
});

const actionIcon = computed(() => (isClickable.value ? CursorClickIcon : BellIcon));

const activate = () => {
    if (isClickable.value) emit('execute', props.action);
};
</script>

<template>
    <article
        class="relative box-border w-full overflow-hidden rounded-2xl border bg-white shadow-[0_1px_3px_rgba(0,0,0,0.1),0_1px_2px_-1px_rgba(0,0,0,0.1)]"
        :class="
            isClickable
                ? 'cursor-pointer border-[#48DDC3] bg-[#F7FFFD] transition-all hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(72,221,195,0.2)] focus:outline-none focus:ring-2 focus:ring-[#48DDC3]'
                : 'border-[#E5E7EB]'
        "
        :role="isClickable ? 'button' : undefined"
        :tabindex="isClickable ? 0 : undefined"
        @click="activate"
        @keydown.enter.prevent="activate"
        @keydown.space.prevent="activate"
    >
        <span class="absolute bottom-0 left-0 top-0 w-1.5 bg-[#48DDC3]" />

        <div class="flex items-start gap-3 px-4 py-4 sm:gap-4 sm:px-6 sm:py-6">
            <div
                class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-[14px] shadow-[0_0_0_1px_#E5E5E5,0_1px_3px_rgba(0,0,0,0.1),0_1px_2px_-1px_rgba(0,0,0,0.1)]"
                :class="isClickable ? 'bg-[#E6FFFA]' : 'bg-[#F5F6F8]'"
            >
                <component
                    :is="actionIcon"
                    class="h-5 w-5"
                    :class="isClickable ? 'text-[#242B59]' : 'text-[#48DDC3]'"
                    aria-hidden="true"
                />
            </div>

            <div class="min-w-0 flex-1">
                <div class="flex flex-wrap items-center gap-2">
                    <h3 class="text-sm font-medium uppercase tracking-[0.7px] text-[#242B59]">
                        {{ $t('aura.cards.action.title') }}
                    </h3>
                    <span
                        class="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.7px]"
                        :style="{ backgroundColor: priority.color, color: priority.text }"
                    >
                        {{ priority.label }}
                    </span>
                    <span
                        v-if="isClickable"
                        class="text-[10px] font-medium uppercase tracking-[0.55px] text-[#159A87]"
                    >
                        {{ $t('aura.cards.action.clickToAnalyze') }}
                    </span>
                </div>

                <AuraRichText
                    class="mt-2 max-w-3xl text-sm font-normal leading-[23px] text-[#626468]"
                    :text="action.description"
                />
            </div>
        </div>
    </article>
</template>
